import { AccessibilityInfo, Appearance, PixelRatio } from "react-native";

import { AppMonitorAttribute } from "@/constants/AppMonitor";
import { setDatadogAttributes } from "@/crossPlatform/Datadog";
import { captureException } from "@/services/AppMonitor";
import { isAndroid, isIOS } from "@/utils/platform.util";

const logDeviceAttributes = async (): Promise<void> => {
  try {
    const [isReduceMotionEnabled, isScreenReaderEnabled] = await Promise.all([
      AccessibilityInfo.isReduceMotionEnabled(),
      AccessibilityInfo.isScreenReaderEnabled(),
    ]);

    // null = not applicable on this platform (DD won't index it, avoiding false "disabled" in dashboards)
    const attributes: Record<
      AppMonitorAttribute,
      boolean | null | number | string | undefined
    > = {
      [AppMonitorAttribute.DeviceFontScale]: PixelRatio.getFontScale(),
      [AppMonitorAttribute.DeviceTheme]: Appearance.getColorScheme() ?? null,
      [AppMonitorAttribute.IsAccessibilityServiceEnabled]: null,
      [AppMonitorAttribute.IsBoldTextEnabled]: null,
      [AppMonitorAttribute.IsGrayscaleEnabled]: null,
      [AppMonitorAttribute.IsHighTextContrastEnabled]: null,
      [AppMonitorAttribute.IsInvertColorsEnabled]: null,
      [AppMonitorAttribute.IsReduceMotionEnabled]: isReduceMotionEnabled,
      [AppMonitorAttribute.IsReduceTransparencyEnabled]: null,
      [AppMonitorAttribute.IsScreenReaderEnabled]: isScreenReaderEnabled,
    };

    if (isIOS()) {
      const [
        isBoldTextEnabled,
        isGrayscaleEnabled,
        isInvertColorsEnabled,
        isReduceTransparencyEnabled,
      ] = await Promise.all([
        AccessibilityInfo.isBoldTextEnabled(),
        AccessibilityInfo.isGrayscaleEnabled(),
        AccessibilityInfo.isInvertColorsEnabled(),
        AccessibilityInfo.isReduceTransparencyEnabled(),
      ]);

      attributes[AppMonitorAttribute.IsBoldTextEnabled] = isBoldTextEnabled;
      attributes[AppMonitorAttribute.IsGrayscaleEnabled] = isGrayscaleEnabled;
      attributes[AppMonitorAttribute.IsInvertColorsEnabled] =
        isInvertColorsEnabled;
      attributes[AppMonitorAttribute.IsReduceTransparencyEnabled] =
        isReduceTransparencyEnabled;
    }

    if (isAndroid()) {
      const [isHighTextContrastEnabled, isAccessibilityServiceEnabled] =
        await Promise.all([
          AccessibilityInfo.isHighTextContrastEnabled(),
          AccessibilityInfo.isAccessibilityServiceEnabled(),
        ]);

      attributes[AppMonitorAttribute.IsHighTextContrastEnabled] =
        isHighTextContrastEnabled;
      attributes[AppMonitorAttribute.IsAccessibilityServiceEnabled] =
        isAccessibilityServiceEnabled;
    }

    await setDatadogAttributes(attributes);
  } catch (error) {
    captureException(error);
  }
};

export default logDeviceAttributes;
